import { compile, e } from "mathjs"
import { EntityTypeRepository } from "../domain/Entities"
import { EntityTypeEditorScreenData } from "../domain/EntityTypeEditorScreenData"
import { generateTextWithAI } from "@/modules/shared/infra/Utils"
import { query_dispatcher_get } from "@/api/client"

export class EntityTypeEditorComponent {

    entity_type_repo:EntityTypeRepository
    screen_data: EntityTypeEditorScreenData
    company_id:number

    constructor(entity_type_repo:EntityTypeRepository,screen_data:EntityTypeEditorScreenData,company_id:number){
        this.entity_type_repo = entity_type_repo
        this.screen_data = screen_data
        this.company_id = company_id
    }

    async mountEditor(type_id:string) {
        let entity_type = this.entity_type_repo.create_empty_entity_type()
        if (type_id) {
            entity_type = await this.entity_type_repo.get_type_by_id(type_id, false)
            console.log(entity_type)
        }
        let available_extra_types = await this.entity_type_repo.get_my_types()
        // Add universal primitive types first
        this.screen_data.available_extra_types = [
            {'name': 'Archivo (File)', 'value': 'file'}
        ]
        // Then add user-defined types
        for( let type of available_extra_types){
            if(type.uuid != entity_type.uuid){
                this.screen_data.available_extra_types.push({'name':type.name, 'value':type.uuid})
            }
        }
        // Load available workflows for CRUD slots
        await this.loadWorkflows()

        // Load CRUD slot schemas from backend
        await this.loadCrudSlotSchemas()

        this.screen_data.entityType = entity_type
    }

    async loadWorkflows() {
        try {
            // Load workflows grouped by CRUD slot operation type
            let res = await query_dispatcher_get.dispatch('get.crud.slot.workflows', {})
            this.screen_data.available_workflows = res || {
                create_update: [],
                get: [],
                delete: [],
                filter: []
            }
        } catch (error) {
            console.error('Failed to load CRUD slot workflows:', error)
            this.screen_data.available_workflows = {
                create_update: [],
                get: [],
                delete: [],
                filter: []
            }
        }
    }

    async loadCrudSlotSchemas() {
        try {
            // Load CRUD slot schemas from workflow editor entity types
            let crudSlotTypes = await this.entity_type_repo.get_aux_workflow_editor_types()

            // Map CRUD slot type UUIDs to operation names
            const uuidToOperation = {
                'crud-slot-filter-params': 'filter',
                'crud-slot-create-update-params': 'create_update',
                'crud-slot-get-params': 'get',
                'crud-slot-delete-params': 'delete'
            }

            for (const slotType of crudSlotTypes) {
                const operation = uuidToOperation[slotType.uuid]
                if (operation) {
                    this.screen_data.crudSlotSchemas[operation] = slotType.schema
                }
            }
        } catch (error) {
            console.error('Failed to load CRUD slot schemas:', error)
        }
    }

    async saveEntityType() {
        this.screen_data.loading = true
        let entity_type = this.screen_data.entityType
        let res = await this.entity_type_repo.save(entity_type)
        if (res['entity_type_id']) {
            let type = await this.entity_type_repo.get_type_by_id(res['entity_type_id'], false)
            this.screen_data.entityType = type
            this.screen_data.success_message('Tipo guardado correctamente')
        } else {
            this.screen_data.error_message('Error al guardar el tipo')
        }
        this.screen_data.loading = false
    }

    async generateUIForSchema(prompt){
        let esquema = this.screen_data.entityType.schema
        let actions_catalog = this.screen_data.entityType.actions_catalog || []

        // Construir descripción de acciones disponibles
        let actions_description = ''
        if (actions_catalog.length > 0) {
            actions_description = '\n\nACCIONES DISPONIBLES:\n'
            actions_description += 'El componente tiene acceso a las siguientes acciones que puede ejecutar mediante run_action(codigo):\n'
            actions_catalog.forEach(action => {
                actions_description += `- run_action("${action.code}"): ${action.description || 'Sin descripción'}\n`
            })
            actions_description += '\nEjemplo de uso: Puedes añadir botones con @click="run_action(\'codigo_accion\')" para ejecutar estas acciones.\n'
        }

        const full_prompt = `Eres un generador de componentes Vue dinámicos. Genera un componente dinámico de Vue 2 usando Vuetify que permita editar un objeto JSON siguiendo este esquema: ${JSON.stringify(esquema, null, 2)}.${actions_description}

El componente debe:
1. Usar v-model con prop "value" y emitir cambios con "input" llamando al método update()
2. Estar dentro de un sistema de compilación dinámica como este:

export function getEditorDynamicComponent(templateString, optionsString) {
    const compiled = Vue.compile(templateString)

    // Parse dynamic options from optionsString
    let dynamicMethods = {}
    let dynamicComputed = {}
    let dynamicData = () => ({})

    if (optionsString) {
        const optionsFn = new Function('return ' + optionsString)
        const options = optionsFn()
        if (options.methods) dynamicMethods = options.methods
        if (options.computed) dynamicComputed = options.computed
        if (options.data) dynamicData = options.data
    }

    return {
        render: compiled.render,
        staticRenderFns: compiled.staticRenderFns,
        props: ["value"],
        data() {
            const dynamicDataObj = typeof dynamicData === 'function' ? dynamicData.call(this) : {}
            return {
                localData: { ...this.value },
                ...dynamicDataObj
            }
        },
        computed: {
            ...dynamicComputed
        },
        methods: {
            update() {
                this.$emit("input", this.localData)
            },
            run_action(action_name){
                this.$emit("run_action", action_name)
            },
            ...dynamicMethods
        },
        components: {
            VCard, VCardTitle, VCardText, VCardActions, VSwitch, VForm,
            VBtn, VTextField, VTextarea, VSpacer, VCol, VRow, VCheckbox,
            VContainer, VIcon, VDataTable, VChip
        }
    }
}

3. Usar componentes de Vuetify (VCard, VTextField, VTextarea, VSelect, VCheckbox, VSwitch, etc.) para cada campo según su tipo
4. Generar automáticamente los campos del formulario para cada propiedad del objeto y actualizar localData dinámicamente
5. Llamar a this.update() en @input de cada campo para emitir cambios al padre
6. NO incluir botón de guardar
7. El tag principal debe contener un solo elemento raíz
8. Puedes aplicar estilos inline con style="..."
9. Ve al grano, es un componente para modelar payloads, sin títulos ni textos explicativos innecesarios

IMPORTANTE sobre component_options:
- En "data", SOLO incluye datos auxiliares para el componente (contadores, flags, estados UI, etc.)
- NO incluyas una copia de localData en data, ya que localData se crea automáticamente desde this.value
- Usa "computed" para propiedades calculadas basadas en localData o data
- Usa "methods" para funciones auxiliares que no sean update() o run_action() (ya definidos)
- El formato debe ser evaluable como objeto JavaScript literal

Ejemplo de component_options:
{
    data: () => ({
        showAdvanced: false,
        selectedTab: 0,
        internalCounter: 0
    }),
    computed: {
        hasErrors() {
            return this.localData.items && this.localData.items.length === 0
        },
        totalAmount() {
            return (this.localData.items || []).reduce((sum, item) => sum + (item.price || 0), 0)
        }
    },
    methods: {
        addItem() {
            if (!this.localData.items) this.localData.items = []
            this.localData.items.push({name: '', price: 0})
            this.update()
        },
        removeItem(index) {
            this.localData.items.splice(index, 1)
            this.update()
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('es-ES', {style: 'currency', currency: 'EUR'}).format(value)
        }
    }
}

Prompt adicional del usuario: ${prompt}`;

        // Define output schema for structured response
        const output_schema = {
            "type": "object",
            "properties": {
                "template": {
                    "type": "string",
                    "description": "HTML template string del componente Vue (sin decoraciones, listo para Vue.compile)"
                },
                "component_options": {
                    "type": "string",
                    "description": "String de JavaScript con el objeto de opciones del componente (data, computed, methods). Debe ser evaluable con new Function('return ' + component_options). NO incluyas localData en data."
                }
            },
            "required": ["template", "component_options"]
        }

        let response = await generateTextWithAI(full_prompt, output_schema, false, false)
        if (response && response['response']){
            const result = response['response']
            this.screen_data.entityType.ui_config['editor_template'] = result.template
            this.screen_data.entityType.ui_config['editor_options'] = result.component_options
            this.screen_data.success_message('UI y opciones generadas correctamente')
        }
        else{
            this.screen_data.error_message('Error al generar la UI')
        }
    }

    async askToModifyUI(prompt){
        let curr_template = this.screen_data.entityType.ui_config['editor_template']
        let curr_options = this.screen_data.entityType.ui_config['editor_options'] || '{}'

        const full_prompt = `Eres un modificador de componentes Vue dinámicos. Modifica el siguiente componente Vue 2 según las instrucciones del usuario.

TEMPLATE ACTUAL:
${curr_template}

COMPONENT OPTIONS ACTUALES:
${curr_options}

INSTRUCCIONES DE MODIFICACIÓN:
${prompt}

Recuerda que el componente usa:
- Vue 2 con Vuetify
- Prop "value" con v-model
- Método update() para emitir cambios (YA VIENE POR DEFECTO, no lo incluyas en component_options)
- Método run_action(action_name) para ejecutar acciones (YA VIENE POR DEFECTO, no lo incluyas en component_options)
- localData se crea automáticamente desde this.value (NO lo incluyas en component_options.data)
- En component_options.data SOLO incluye datos auxiliares adicionales (contadores, flags, estados UI, etc.)
- Puedes usar computed para propiedades calculadas
- Puedes usar methods para funciones auxiliares ADICIONALES (no update ni run_action)

Devuelve el template y component_options modificados.`;

        // Define output schema for structured response
        const output_schema = {
            "type": "object",
            "properties": {
                "template": {
                    "type": "string",
                    "description": "HTML template string modificado del componente Vue"
                },
                "component_options": {
                    "type": "string",
                    "description": "String de JavaScript con el objeto de opciones modificado (data, computed, methods). NO incluyas localData en data."
                }
            },
            "required": ["template", "component_options"]
        }

        let response = await generateTextWithAI(full_prompt, output_schema, false, false)
        if (response && response['response']){
            const result = response['response']
            this.screen_data.entityType.ui_config['editor_template'] = result.template
            this.screen_data.entityType.ui_config['editor_options'] = result.component_options
            this.screen_data.success_message('UI y opciones modificadas correctamente')
        }
        else{
            this.screen_data.error_message('Error al modificar la UI')
        }
    }
}